import React, { useState } from 'react';
import { Building2, Calendar, MapPin, ChevronDown, ChevronUp } from 'lucide-react';
import { experience } from '../data/mock';
import { Badge } from './ui/badge';
import { Card } from './ui/card';

const ExperienceNew = () => {
  const [expandedId, setExpandedId] = useState(null);

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id="experience" className="py-20 bg-[#fffcf6] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#d9b896]/20 to-transparent blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-gradient-to-br from-[#e0d0b8]/30 to-transparent blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <p className="font-handwriting text-3xl mb-2 gradient-text">My journey so far</p>
          <h2 className="text-4xl md:text-6xl font-bold text-[#2d2a26] mb-4">
            <span className="font-serif">Professional </span>
            <span className="font-display gradient-text-animated">Experience</span>
          </h2>
          <p className="text-lg text-[#737373] max-w-2xl mx-auto">
            From code to C-suite conversations
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-[#b88865] via-[#8b5a3c] to-[#6b4226]"></div>

          {/* Experience items */}
          <div className="space-y-12">
            {experience.map((exp, index) => (
              <div 
                key={exp.id}
                className={`relative flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
              >
                {/* Timeline dot */}
                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-4 h-4 gradient-bg rounded-full border-4 border-[#fffcf6] z-10 shadow-lg"></div>

                {/* Content card */}
                <div className={`w-full md:w-[calc(50%-2rem)] ${index % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                  <Card data-testid={`experience-card-${exp.id}`} className="bg-white/80 backdrop-blur-sm border-[#e0d0b8] hover:border-[#8b5a3c]/40 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden group">
                    <div className="p-6">
                      {/* Company & Role */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <Building2 className="w-5 h-5 text-[#8b5a3c]" />
                            <h3 className="text-xl font-bold text-[#2d2a26] font-serif">{exp.company}</h3>
                          </div>
                          <h4 className="text-lg font-semibold mb-2 font-display gradient-text">{exp.role}</h4>
                        </div>
                      </div>

                      {/* Meta info */}
                      <div className="flex flex-wrap gap-4 mb-4 text-sm text-[#737373]">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{exp.period}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{exp.location}</span>
                        </div>
                      </div>

                      {/* Description */}
                      <p className="text-[#555555] mb-4 leading-relaxed">{exp.description}</p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {exp.tags.slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} className="bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] border border-[#8b5a3c]/25 hover:from-[#b88865]/25 hover:to-[#6b4226]/25">
                            {tag}
                          </Badge>
                        ))}
                        {exp.tags.length > 3 && (
                          <Badge className="bg-gradient-to-r from-[#b88865]/15 to-[#6b4226]/15 text-[#6b4226] border border-[#8b5a3c]/25">
                            +{exp.tags.length - 3}
                          </Badge>
                        )}
                      </div>

                      {/* Expand button */}
                      <button
                        data-testid={`experience-expand-${exp.id}`}
                        onClick={() => toggleExpand(exp.id)}
                        className="flex items-center gap-2 text-[#8b5a3c] hover:text-[#6b4226] transition-colors text-sm font-medium"
                      >
                        {expandedId === exp.id ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Show less
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            View key achievements
                          </>
                        )}
                      </button>

                      {/* Expanded content */}
                      {expandedId === exp.id && (
                        <div className="mt-4 pt-4 border-t border-[#e0d0b8] animate-in fade-in slide-in-from-top-2 duration-300">
                          <h5 className="text-sm font-semibold text-[#555555] mb-3">Key Achievements:</h5>
                          <ul className="space-y-2">
                            {exp.highlights.map((highlight, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-[#666666]">
                                <span className="text-[#8b5a3c] mt-1">▹</span>
                                <span>{highlight}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceNew;
